import { useMemo, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn, formatDate } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react';
import { Button } from '@/components/ui/button';

// ── Types ──────────────────────────────────────────────────────
interface GanttTask {
  id: string;
  title: string;
  dueDate: string | null;
  startDate?: string | null;
  estimatedHours?: number | null;
  status: string;
  priority: string;
  parentId?: string | null;
  assignee: { id: string; name: string } | null;
  projectId: string;
  projectName: string;
}

interface GanttMilestone {
  id: string;
  name: string;
  dueDate: string | null;
  completedAt: string | null;
  projectId: string;
  projectName: string;
}

interface GanttChartProps {
  tasks: GanttTask[];
  milestones: GanttMilestone[];
  projectStartDate?: string | null;
  projectTargetDate?: string | null;
}

// ── Status colors ───────────────────────────────────────────────
const statusBarColors: Record<string, { bg: string; fill: string }> = {
  todo:        { bg: '#1e293b', fill: '#475569' },
  in_progress: { bg: '#1e3a5f', fill: '#3b82f6' },
  review:      { bg: '#3a2f1e', fill: '#eab308' },
  done:        { bg: '#143d2b', fill: '#10b981' },
};

const milestoneColor = '#8b5cf6'; // purple

const statusLabels: Record<string, string> = {
  todo: 'To Do',
  in_progress: 'In Progress',
  review: 'Review',
  done: 'Done',
};

// ── Helpers ─────────────────────────────────────────────────────
function parseDate(d: string | null | undefined): Date | null {
  if (!d) return null;
  const date = new Date(d);
  return isNaN(date.getTime()) ? null : date;
}

function daysBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24));
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/** Estimate a start date for a task that has dueDate but no explicit start */
function estimateStart(task: GanttTask): Date | null {
  const due = parseDate(task.dueDate);
  if (!due) return null;

  // If task has estimated hours, estimate ~8 hours per day
  if (task.estimatedHours) {
    const durationDays = Math.max(1, Math.ceil(task.estimatedHours / 8));
    return addDays(due, -durationDays);
  }

  // Default: start 1 day before due date
  return addDays(due, -1);
}

/** Get the effective start date for drawing a bar */
function getBarStart(task: GanttTask, globalStart: Date): Date | null {
  const explicitStart = parseDate(task.startDate);
  if (explicitStart) return explicitStart >= globalStart ? explicitStart : globalStart;

  const estimated = estimateStart(task);
  if (estimated) return estimated >= globalStart ? estimated : globalStart;

  return null;
}

/** Get the end date for drawing a bar */
function getBarEnd(task: GanttTask): Date | null {
  return parseDate(task.dueDate);
}

// ── Component ──────────────────────────────────────────────────
export function GanttChart({ tasks, milestones, projectStartDate, projectTargetDate }: GanttChartProps) {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [dayWidth, setDayWidth] = useState(28); // pixels per day
  const [scrollOffset, setScrollOffset] = useState(0);

  // Compute the global date range
  const { globalStart, globalEnd, totalDays } = useMemo(() => {
    let min = new Date();
    let max = addDays(new Date(), 30);

    const allDates: Date[] = [];

    if (projectStartDate) {
      const d = parseDate(projectStartDate);
      if (d) allDates.push(d);
    }
    if (projectTargetDate) {
      const d = parseDate(projectTargetDate);
      if (d) allDates.push(d);
    }

    for (const t of tasks) {
      const due = parseDate(t.dueDate);
      if (due) allDates.push(due);
      const start = parseDate(t.startDate) || estimateStart(t);
      if (start) allDates.push(start);
    }

    for (const m of milestones) {
      const due = parseDate(m.dueDate);
      if (due) allDates.push(due);
    }

    if (allDates.length > 0) {
      // Add 7-day padding on each side
      min = addDays(new Date(Math.min(...allDates.map((d) => d.getTime()))), -3);
      max = addDays(new Date(Math.max(...allDates.map((d) => d.getTime()))), 3);
    }

    // If still no dates, use a default 60-day range
    if (allDates.length === 0) {
      min = new Date();
      max = addDays(new Date(), 60);
    }

    const total = daysBetween(min, max);
    return { globalStart: min, globalEnd: max, totalDays: total || 30 };
  }, [tasks, milestones, projectStartDate, projectTargetDate]);

  // Compute row data
  const rows = useMemo(() => {
    const result: Array<{
      id: string;
      type: 'task' | 'milestone';
      title: string;
      barStart: Date | null;
      barEnd: Date | null;
      barLeftPct: number;
      barWidthPct: number;
      status: string;
      priority: string;
      parentId: string | null;
      assignee: { id: string; name: string } | null;
      projectId: string;
      completedAt: string | null;
    }> = [];

    for (const t of tasks) {
      const start = getBarStart(t, globalStart);
      const end = getBarEnd(t);
      if (!end) continue;

      const startDay = start ? daysBetween(globalStart, start) : 0;
      const duration = start ? daysBetween(start, end) : daysBetween(globalStart, end);
      const leftPct = (startDay / totalDays) * 100;
      const widthPct = Math.max((duration / totalDays) * 100, 2); // at least 2% wide

      result.push({
        id: `task-${t.id}`,
        type: 'task',
        title: t.title,
        barStart: start,
        barEnd: end,
        barLeftPct: leftPct,
        barWidthPct: widthPct,
        status: t.status,
        priority: t.priority,
        parentId: t.parentId || null,
        assignee: t.assignee,
        projectId: t.projectId,
        completedAt: null,
      });
    }

    for (const m of milestones) {
      const msDate = parseDate(m.dueDate);
      if (!msDate) continue;

      const dayOffset = daysBetween(globalStart, msDate);
      const leftPct = (dayOffset / totalDays) * 100;

      result.push({
        id: `ms-${m.id}`,
        type: 'milestone',
        title: m.name,
        barStart: msDate,
        barEnd: msDate,
        barLeftPct: leftPct,
        barWidthPct: 0,
        status: m.completedAt ? 'done' : 'pending',
        priority: '',
        parentId: null,
        assignee: null,
        projectId: m.projectId,
        completedAt: m.completedAt,
      });
    }

    // Sort by: milestones first (higher), then by due date ascending
    result.sort((a, b) => {
      if (a.type !== b.type) return a.type === 'milestone' ? -1 : 1;
      const aDate = a.barEnd?.getTime() || 0;
      const bDate = b.barEnd?.getTime() || 0;
      return aDate - bDate;
    });

    return result;
  }, [tasks, milestones, globalStart, totalDays]);

  // Compute parent-child dependency arrows
  const dependencyArrows = useMemo(() => {
    const arrows: Array<{ fromIdx: number; toIdx: number }> = [];

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (!row.parentId || row.type !== 'task') continue;

      // Find the parent row
      const parentIdx = rows.findIndex(
        (r) => r.type === 'task' && r.id === `task-${row.parentId}`
      );
      if (parentIdx >= 0) {
        arrows.push({ fromIdx: i, toIdx: parentIdx });
      }
    }

    return arrows;
  }, [rows]);

  // Generate month headers
  const monthHeaders = useMemo(() => {
    const months: Array<{ label: string; leftPct: number; widthPct: number }> = [];
    let current = new Date(globalStart);
    current.setDate(1);

    while (current <= globalEnd) {
      const monthStart = new Date(current);
      const monthEnd = new Date(current.getFullYear(), current.getMonth() + 1, 0);

      const actualStart = monthStart < globalStart ? globalStart : monthStart;
      const actualEnd = monthEnd > globalEnd ? globalEnd : monthEnd;

      const startDay = daysBetween(globalStart, actualStart);
      const endDay = daysBetween(globalStart, actualEnd);

      months.push({
        label: current.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }),
        leftPct: (startDay / totalDays) * 100,
        widthPct: ((endDay - startDay + 1) / totalDays) * 100,
      });

      current.setMonth(current.getMonth() + 1);
    }

    return months;
  }, [globalStart, globalEnd, totalDays]);

  // Generate week gridlines
  const weekGridlines = useMemo(() => {
    const lines: Array<{ leftPct: number }> = [];
    let current = new Date(globalStart);
    // Align to Monday
    const dayOfWeek = current.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    current = addDays(current, diff);

    while (current <= globalEnd) {
      const dayOffset = daysBetween(globalStart, current);
      if (dayOffset > 0 && dayOffset < totalDays) {
        lines.push({ leftPct: (dayOffset / totalDays) * 100 });
      }
      current = addDays(current, 7);
    }
    return lines;
  }, [globalStart, globalEnd, totalDays]);

  // Today line
  const todayLine = useMemo(() => {
    const today = new Date();
    if (today < globalStart || today > globalEnd) return null;
    const dayOffset = daysBetween(globalStart, today);
    return (dayOffset / totalDays) * 100;
  }, [globalStart, globalEnd, totalDays]);

  const ROW_HEIGHT = 44;
  const HEADER_HEIGHT = 44;
  const LABEL_WIDTH = 260;

  return (
    <div className="rounded-lg border border-[var(--border-color)] overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--border-color)] bg-[var(--bg-secondary)]">
        <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
          <span className="font-medium text-[var(--text-primary)]">Gantt Chart</span>
          <span className="text-xs">
            {formatDate(globalStart)} — {formatDate(globalEnd)}
          </span>
          <span className="text-xs text-[var(--text-secondary)]">
            · {rows.length} item{rows.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => setDayWidth((w) => Math.max(12, w - 4))}
            title="Zoom out"
          >
            <ZoomOut size={14} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => setDayWidth((w) => Math.min(60, w + 4))}
            title="Zoom in"
          >
            <ZoomIn size={14} />
          </Button>
        </div>
      </div>

      {/* Chart area */}
      <div className="flex" style={{ maxHeight: '60vh' }}>
        {/* Left column — row labels */}
        <div className="flex-shrink-0 border-r border-[var(--border-color)] bg-[var(--bg-secondary)]/50" style={{ width: LABEL_WIDTH }}>
          {/* Empty header */}
          <div style={{ height: HEADER_HEIGHT }} className="border-b border-[var(--border-color)]" />
          {/* Row labels */}
          <div className="overflow-hidden" style={{ height: `calc(60vh - ${HEADER_HEIGHT}px)` }}>
            {rows.map((row) => (
              <div
                key={row.id}
                className={cn(
                  'flex items-center gap-2 px-3 border-b border-[var(--border-color)]/50 cursor-pointer hover:bg-[var(--bg-primary)]/50 transition-colors',
                  row.type === 'milestone' && 'bg-purple-500/5'
                )}
                style={{ height: ROW_HEIGHT }}
                onClick={() => {
                  if (row.type === 'task') navigate(`/tasks/${row.id.replace('task-', '')}`);
                }}
                title={row.type === 'milestone' ? `Milestone: ${row.title}` : `Task: ${row.title}`}
              >
                <div className="flex-1 min-w-0 flex items-center gap-1.5">
                  {row.type === 'milestone' && (
                    <span className="text-purple-400 text-xs flex-shrink-0">◆</span>
                  )}
                  <span className={cn(
                    'text-xs truncate',
                    row.type === 'milestone' ? 'font-semibold text-purple-300' : 'font-medium'
                  )}>
                    {row.title}
                  </span>
                </div>
                {row.assignee && (
                  <span className="text-[10px] text-[var(--text-secondary)] flex-shrink-0 truncate max-w-[60px]">
                    {row.assignee.name}
                  </span>
                )}
                {row.type === 'task' && row.status && (
                  <Badge variant="outline" className={cn(
                    'text-[9px] px-1 py-0 flex-shrink-0',
                    row.status === 'todo' && 'text-gray-400 border-gray-500/30',
                    row.status === 'in_progress' && 'text-blue-400 border-blue-500/30',
                    row.status === 'review' && 'text-yellow-400 border-yellow-500/30',
                    row.status === 'done' && 'text-dyndev-green border-dyndev-green/30',
                  )}>
                    {statusLabels[row.status] || row.status}
                  </Badge>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Right column — timeline */}
        <div className="flex-1 overflow-auto" ref={scrollRef}>
          <svg
            width={totalDays * dayWidth + 20}
            height={HEADER_HEIGHT + rows.length * ROW_HEIGHT}
            style={{ display: 'block', minWidth: '100%' }}
          >
            {/* Month headers */}
            {monthHeaders.map((mh, i) => (
              <g key={i}>
                <rect
                  x={(mh.leftPct / 100) * totalDays * dayWidth}
                  y={0}
                  width={(mh.widthPct / 100) * totalDays * dayWidth}
                  height={HEADER_HEIGHT}
                  fill="var(--bg-secondary)"
                  stroke="var(--border-color)"
                  strokeWidth={0.5}
                />
                <text
                  x={((mh.leftPct + mh.widthPct / 2) / 100) * totalDays * dayWidth}
                  y={HEADER_HEIGHT / 2 + 1}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="var(--text-secondary)"
                  fontSize={11}
                  fontWeight={600}
                >
                  {mh.label}
                </text>
              </g>
            ))}

            {/* Week gridlines */}
            {weekGridlines.map((gl, i) => (
              <line
                key={i}
                x1={(gl.leftPct / 100) * totalDays * dayWidth}
                y1={HEADER_HEIGHT}
                x2={(gl.leftPct / 100) * totalDays * dayWidth}
                y2={HEADER_HEIGHT + rows.length * ROW_HEIGHT}
                stroke="var(--border-color)"
                strokeWidth={0.5}
                opacity={0.4}
              />
            ))}

            {/* Today line */}
            {todayLine !== null && (
              <line
                x1={(todayLine / 100) * totalDays * dayWidth}
                y1={0}
                x2={(todayLine / 100) * totalDays * dayWidth}
                y2={HEADER_HEIGHT + rows.length * ROW_HEIGHT}
                stroke="#10b981"
                strokeWidth={2}
                strokeDasharray="6,3"
                opacity={0.7}
              />
            )}

            {/* Dependency arrows (drawn first so they appear behind bars) */}
            {dependencyArrows.map((arrow, i) => {
              const fromRow = rows[arrow.fromIdx];
              const toRow = rows[arrow.toIdx];

              if (!fromRow.barEnd || !toRow.barEnd) return null;

              const fromY = HEADER_HEIGHT + arrow.fromIdx * ROW_HEIGHT + ROW_HEIGHT / 2;
              const toY = HEADER_HEIGHT + arrow.toIdx * ROW_HEIGHT + ROW_HEIGHT / 2;

              // Arrow starts from the right edge of the child's bar
              const fromX = (fromRow.barLeftPct + fromRow.barWidthPct / 100 / 2) / 100 * totalDays * dayWidth;
              // Arrow goes to the left edge of the parent's bar
              const toX = ((toRow.barLeftPct) / 100) * totalDays * dayWidth;

              // Only draw if positions differ (arrows work both directions)
              if (Math.abs(fromX - toX) < 5) return null;

              const midY = (fromY + toY) / 2;
              const midX = Math.max(fromX, toX) + 15;

              return (
                <g key={i}>
                  <path
                    d={`M ${fromX} ${fromY} C ${fromX + 20} ${fromY}, ${midX} ${midY}, ${toX} ${toY}`}
                    fill="none"
                    stroke="#8b5cf6"
                    strokeWidth={1.5}
                    strokeDasharray="4,3"
                    opacity={0.6}
                  />
                  {/* Arrowhead */}
                  <polygon
                    points={`${toX - 4},${toY - 4} ${toX},${toY} ${toX - 4},${toY + 4}`}
                    fill="#8b5cf6"
                    opacity={0.6}
                  />
                </g>
              );
            })}

            {/* Row background alternation */}
            {rows.map((_, i) => (
              i % 2 === 0 ? (
                <rect
                  key={i}
                  x={0}
                  y={HEADER_HEIGHT + i * ROW_HEIGHT}
                  width={totalDays * dayWidth}
                  height={ROW_HEIGHT}
                  fill="var(--bg-primary)"
                  opacity={0.3}
                />
              ) : null
            ))}

            {/* Task bars and milestone markers */}
            {rows.map((row, i) => {
              const y = HEADER_HEIGHT + i * ROW_HEIGHT + 10;
              const barHeight = ROW_HEIGHT - 20;

              if (row.type === 'milestone') {
                // Diamond marker
                const cx = (row.barLeftPct / 100) * totalDays * dayWidth;
                const cy = y + barHeight / 2;
                const size = 8;
                const isComplete = !!row.completedAt;
                return (
                  <g key={row.id}>
                    <polygon
                      points={`${cx},${cy - size} ${cx + size},${cy} ${cx},${cy + size} ${cx - size},${cy}`}
                      fill={isComplete ? '#10b981' : milestoneColor}
                      opacity={0.9}
                    />
                    <line
                      x1={cx}
                      y1={cy + size + 2}
                      x2={cx}
                      y2={y + barHeight}
                      stroke={milestoneColor}
                      strokeWidth={1}
                      opacity={0.3}
                    />
                  </g>
                );
              }

              // Task bar
              if (!row.barEnd) return null;

              const barX = (row.barLeftPct / 100) * totalDays * dayWidth;
              const barWidth = (row.barWidthPct / 100) * totalDays * dayWidth;
              const barColors = statusBarColors[row.status] || statusBarColors.todo;

              const isOverdue = row.barEnd < new Date() && row.status !== 'done';

              return (
                <g key={row.id}>
                  {/* Bar shadow */}
                  <rect
                    x={barX}
                    y={y + 2}
                    width={Math.max(barWidth, 6)}
                    height={barHeight}
                    rx={4}
                    fill={barColors.fill}
                    opacity={0.15}
                  />
                  {/* Main bar */}
                  <rect
                    x={barX}
                    y={y}
                    width={Math.max(barWidth, 6)}
                    height={barHeight}
                    rx={4}
                    fill={barColors.fill}
                    opacity={isOverdue ? 0.9 : 0.75}
                    stroke={isOverdue ? '#ef4444' : 'none'}
                    strokeWidth={isOverdue ? 1.5 : 0}
                  />
                  {/* Progress indicator for done tasks */}
                  {row.status === 'done' && (
                    <rect
                      x={barX}
                      y={y}
                      width={Math.max(barWidth, 6)}
                      height={barHeight}
                      rx={4}
                      fill="none"
                      stroke="#10b981"
                      strokeWidth={1.5}
                      strokeDasharray="4,2"
                    />
                  )}
                </g>
              );
            })}
          </svg>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 px-4 py-2 border-t border-[var(--border-color)] bg-[var(--bg-secondary)]/50 text-[10px] text-[var(--text-secondary)]">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded" style={{ background: '#3b82f6', opacity: 0.75 }} />
          In Progress
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded" style={{ background: '#475569', opacity: 0.75 }} />
          To Do
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded" style={{ background: '#eab308', opacity: 0.75 }} />
          Review
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded" style={{ background: '#10b981', opacity: 0.75 }} />
          Done
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5" style={{
            background: milestoneColor,
            clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
          }} />
          Milestone
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-4 h-0.5 rounded" style={{ background: '#8b5cf6', opacity: 0.6 }} />
          Dependency
        </span>
        {todayLine !== null && (
          <span className="flex items-center gap-1.5">
            <span className="w-4 h-0.5" style={{ background: '#10b981', borderTop: '2px dashed #10b981', height: 0 }} />
            Today
          </span>
        )}
      </div>

      {/* Empty state */}
      {rows.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="text-sm text-[var(--text-secondary)] mb-1">No timeline data</p>
          <p className="text-xs text-[var(--text-secondary)]">
            Tasks with due dates and milestones will appear here as a Gantt chart.
          </p>
        </div>
      )}
    </div>
  );
}
